import { useMemo, useRef } from 'react';
import { DefaultButton, DialogFooter, Label, PrimaryButton, Spinner, SpinnerSize } from '@fluentui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { USER_OVERRIDES } from '../../context/network/http/QueryProvider/queryKeys';
import { SettingsOverrideType, SettingsType, SubSettingsOverrides, UserSettingsOverrides } from '../../api/generated/data-contracts';
import { ApiCalls } from '../../api/api';
import { changeCase } from '../../helpers/changeKeysCase';
import { useUISettings } from '../../context/network/http/QueryProvider/queries/UISettings';
import { FluentLabel } from 'spark-forms-fluent8';
import { TPBuilder } from '../../forms/_builders/new/TPBuilder';
import { useForm } from 'spark-forms';
import { userOverridesSchema } from './userOverridesSchema';
import { useTranslation } from '../../Locales/i18n/localizationService';

export const UserSettingsOverridesView = (options: { callback: () => void }) => {
	const { t } = useTranslation();
    const { data: uiSettings } = useUISettings();

    const userOverrideFields = uiSettings.settings.userSettingPropertiesAvailable;
    const userSettingsEnabled = !!userOverrideFields?.length;
	const changes = useRef(false);

    const resourceId = uiSettings.resource.id;
    const queryKey = useMemo(() => [USER_OVERRIDES, resourceId] as const, [resourceId]);

    // Fetch the user override from the API
    const { data: userOverride, isFetching, refetch } = useQuery<SubSettingsOverrides>({
        queryKey,
        queryFn: async () => {
            const response = await ApiCalls.getUserOverride(resourceId);
            return (
                changeCase.toCamel(response.data) ?? {
                    id: resourceId,
                    name: uiSettings.resource.name,
                    overrides: {},
                    settingsType: SettingsType.Overrides,
                    overrideType: SettingsOverrideType.User,
                }
            );
        },
        initialData: {
            id: resourceId,
            name: uiSettings.resource.name,
            overrides: {},
            settingsType: SettingsType.Overrides,
            overrideType: SettingsOverrideType.User,
        },
    });

    // Initialize local state with the fetched data
    const localUserOverride = useRef(userOverride);

    // Create a mutation to upsert the user override
    const upsertMutation = useMutation({
        mutationFn: (overrideData: UserSettingsOverrides) => {
			const userOverride = { ...overrideData, settingsType: SettingsType.Overrides, overrideType: SettingsOverrideType.User } as UserSettingsOverrides;
			return ApiCalls.upsertSettingsOverride({ subOverride: userOverride })
		}
    });

    return (
        <>
            {(isFetching || upsertMutation.isPending) && <Spinner size={SpinnerSize.large} label={t("Common.LoadingDot")} />}
            {!userSettingsEnabled && <Label>{t("UserSettings.AdminDoesNotAllowPersonalSettings")}</Label>}
            {!(isFetching || upsertMutation.isPending) && userSettingsEnabled && (
                <UserOverridesForm item={userOverride} fieldsWhitelist={userOverrideFields} onItemChange={item => {
					localUserOverride.current = item;
					changes.current = true;
				}} />
            )}
            <DialogFooter>
                <PrimaryButton
                    onClick={() => {
						if (changes) {
							upsertMutation.mutate(localUserOverride.current);
						}
                        options.callback();
                    }}
                    text={'Save'}
                />
                <DefaultButton onClick={() => options.callback()} text={'Cancel'} />
				<DefaultButton onClick={async () => {
					await ApiCalls.resetUserOverride(resourceId);
					refetch();
				}} text={'Revert to defaults'} />
            </DialogFooter>
        </>
    );
};

export const UserOverridesForm = ({
    item,
    fieldsWhitelist,
    onItemChange,
}: {
    item: SubSettingsOverrides;
    fieldsWhitelist: string[];
    onItemChange: (updatedItem: SubSettingsOverrides) => void;
}) => {
    const localItem = useRef(item).current;

    const builderOptions = useMemo(
        () => ({
            labelComponent: FluentLabel,
        }),
        [],
    );

    const schema = useMemo(() => {
        return userOverridesSchema(fieldsWhitelist);
    }, [fieldsWhitelist]);

    const { Form } = useForm({
        item: localItem,
        builderOptions,
        onChange: onItemChange,
        schema: schema,
        builder: TPBuilder,
    });

    return <Form />;
};
